import { useMemo } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import type { SelectionTabId, SelectedImageData } from './imageInsertTypes';
import { UploadPanel } from './UploadPanel';
import type { UploadPanelState } from './UploadPanel';
import { AIGeneratePanel } from './AIGeneratePanel';
import type { AIGenerateState } from './AIGeneratePanel';
import { LibraryBrowseView } from './LibraryBrowseView';
import type { LibraryPanelState } from './libraryPanelTypes';
import { useLibraryPanelController } from './useLibraryPanelController';
import { ModalFooter } from './ModalFooter';

interface ImageSelectionViewProps {
  activeTab: SelectionTabId;
  onTabChange: (tab: SelectionTabId) => void;
  onImageReady: (data: SelectedImageData) => void;
  uploadState: UploadPanelState;
  onUploadStateChange: (state: UploadPanelState) => void;
  aiGenerateState: AIGenerateState;
  onAIGenerateStateChange: (state: AIGenerateState) => void;
  onGenerate: (prompt: string) => Promise<{ imageUrl: string; mimeType: string }>;
  libraryState: LibraryPanelState;
  onLibraryStateChange: (state: LibraryPanelState) => void;
  targetAspectRatio?: string;
  pendingRemoval?: boolean;
  onConfirmRemoval?: () => void;
  onClose: () => void;
}

const TABS: { id: SelectionTabId; label: string }[] = [
  { id: 'upload', label: 'Upload' },
  { id: 'ai-generate', label: 'Generate with AI' },
  { id: 'browse-library', label: 'Browse Library' },
];

export function ImageSelectionView({
  activeTab,
  onTabChange,
  onImageReady,
  uploadState,
  onUploadStateChange,
  aiGenerateState,
  onAIGenerateStateChange,
  onGenerate,
  libraryState,
  onLibraryStateChange,
  targetAspectRatio,
  pendingRemoval = false,
  onConfirmRemoval,
  onClose,
}: ImageSelectionViewProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);

  const libraryController = useLibraryPanelController({
    state: libraryState,
    onStateChange: onLibraryStateChange,
    onImageReady,
  });

  return (
    <>
      <div style={styles.tabBar} role="tablist" aria-label="Image source tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            id={`tab-${tab.id}`}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
            }}
            onClick={() => onTabChange(tab.id)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        id={`tabpanel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`tab-${activeTab}`}
        style={styles.tabContent}
      >
        {activeTab === 'upload' && (
          <UploadPanel
            state={uploadState}
            onStateChange={onUploadStateChange}
            onImageReady={onImageReady}
          />
        )}
        {activeTab === 'ai-generate' && (
          <AIGeneratePanel
            state={aiGenerateState}
            onStateChange={onAIGenerateStateChange}
            onGenerate={onGenerate}
            onImageReady={onImageReady}
            targetAspectRatio={targetAspectRatio}
          />
        )}
        {activeTab === 'browse-library' && (
          <LibraryBrowseView
            state={libraryState}
            onSearchChange={libraryController.handleSearchChange}
            onImageSelect={libraryController.handleImageSelect}
            onImageDoubleClick={libraryController.handleImageDoubleClick}
            onSubTabChange={libraryController.handleSubTabChange}
            onUseImage={libraryController.handleUseImage}
            onLoadMore={libraryController.handleLoadMore}
            onRetry={libraryController.handleRetry}
          />
        )}
      </div>

      {pendingRemoval && onConfirmRemoval && (
        <ModalFooter
          onCancel={onClose}
          actions={[
            {
              label: 'Save Changes',
              onClick: onConfirmRemoval,
              disabled: false,
              ariaLabel: 'Remove image and save',
              variant: 'primary',
            },
          ]}
        />
      )}
    </>
  );
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, React.CSSProperties> {
  return {
    tabBar: {
      display: 'flex',
      borderBottom: `1px solid ${tokens.text.subtle}`,
      padding: '0 20px',
    },
    tab: {
      padding: '12px 16px',
      fontSize: '14px',
      fontWeight: 500,
      color: tokens.text.muted,
      background: 'none',
      border: 'none',
      borderBottomWidth: '2px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'transparent',
      cursor: 'pointer',
      transition: `color ${tokens.ui.transitionDefault}, border-color ${tokens.ui.transitionDefault}`,
      marginBottom: '-1px',
      minWidth: '44px',
      minHeight: '44px',
      whiteSpace: 'nowrap',
    },
    tabActive: {
      color: tokens.ui.selectionBorder,
      borderBottomColor: tokens.ui.selectionBorder,
    },
    tabContent: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      overflow: 'hidden',
    },
  };
}
